import React, { useMemo, useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Link } from 'react-router-dom';
import { BillingInterval, PublicPlan } from './SaasApi';
import {
    getPremiumCapacity,
    getPremiumCapacityForFamilies,
    getPremiumCapacityForSeats,
    getPremiumTotalPrice,
    PremiumCapacity,
    PremiumCapacityConfig,
} from './premiumCapacity';
import { PublicError, PublicLayout, PublicLoading } from './PublicLayout';
import { usePublicPlanCatalog } from './usePublicPlanCatalog';

const currencyFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const formatPrice = (price: number): string => currencyFormatter.format(price);

const Check = () => (
    <span className='saas-check' aria-hidden='true'>
        <CheckRoundedIcon fontSize='inherit' />
    </span>
);

type CapacityControlProps = {
    id: string;
    label: string;
    help: string;
    value: string;
    minimum: number;
    step: number;
    decreaseDisabled: boolean;
    increaseDisabled: boolean;
    onChange: (value: string) => void;
    onCommit: () => void;
    onDecrease: () => void;
    onIncrease: () => void;
};

// Render one accessible numeric stepper while the parent keeps both controls synchronized.
const CapacityControl: React.FC<CapacityControlProps> = ({
    id,
    label,
    help,
    value,
    minimum,
    step,
    decreaseDisabled,
    increaseDisabled,
    onChange,
    onCommit,
    onDecrease,
    onIncrease,
}) => (
    <div className='saas-capacity-row'>
        <div>
            <label htmlFor={id}>{label}</label>
            <p id={`${id}-help`}>{help}</p>
        </div>
        <div className='saas-capacity-stepper'>
            <button
                type='button'
                aria-label={`Decrease ${label.toLowerCase()}`}
                disabled={decreaseDisabled}
                onClick={onDecrease}
            >−</button>
            <input
                id={id}
                type='number'
                inputMode='numeric'
                min={minimum}
                step={step}
                value={value}
                aria-describedby={`${id}-help capacity-adjustment-message`}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onCommit}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                }}
            />
            <button
                type='button'
                aria-label={`Increase ${label.toLowerCase()}`}
                disabled={increaseDisabled}
                onClick={onIncrease}
            >+</button>
        </div>
    </div>
);

// Present Premium as one scalable plan rather than several artificial fixed tiers.
const PremiumConfigurator: React.FC<{
    plan: PublicPlan;
    interval: BillingInterval;
    onIntervalChange: (interval: BillingInterval) => void;
    navigationTarget?: '_top';
}> = ({ plan, interval, onIntervalChange, navigationTarget }) => {
    const config = useMemo<PremiumCapacityConfig>(() => ({
        dashboardSeatsPerUnit: plan.dashboardSeatsPerUnit,
        familyLicensesPerUnit: plan.familyLicensesPerUnit,
        minimumUnits: plan.minimumUnits,
        maximumSelfServeUnits: plan.maximumSelfServeUnits,
    }), [
        plan.dashboardSeatsPerUnit,
        plan.familyLicensesPerUnit,
        plan.maximumSelfServeUnits,
        plan.minimumUnits,
    ]);
    const initialCapacity = useMemo(
        () => getPremiumCapacity(config.minimumUnits, config),
        [config]
    );
    const [capacity, setCapacity] = useState<PremiumCapacity>(initialCapacity);
    const [seatInput, setSeatInput] = useState(String(initialCapacity.dashboardSeats));
    const [familyInput, setFamilyInput] = useState(String(initialCapacity.familyLicenses));
    const [adjustmentMessage, setAdjustmentMessage] = useState('');

    const applyCapacity = (nextCapacity: PremiumCapacity, message = '') => {
        setCapacity(nextCapacity);
        setSeatInput(String(nextCapacity.dashboardSeats));
        setFamilyInput(String(nextCapacity.familyLicenses));
        setAdjustmentMessage(message);
    };

    const selectUnits = (units: number) => {
        applyCapacity(getPremiumCapacity(units, config));
    };

    const commitSeats = () => {
        const requestedSeats = Number(seatInput);
        if (!seatInput.trim() || !Number.isFinite(requestedSeats)) {
            applyCapacity(capacity);
            return;
        }
        const nextCapacity = getPremiumCapacityForSeats(requestedSeats, config);
        const rounded = nextCapacity.dashboardSeats !== requestedSeats;
        applyCapacity(
            nextCapacity,
            rounded
                ? `${requestedSeats} dashboard seats requires ${nextCapacity.units} whole unit${nextCapacity.units === 1 ? '' : 's'}. Rounded up to ${nextCapacity.dashboardSeats} seats and ${nextCapacity.familyLicenses} family licences.`
                : ''
        );
    };

    const commitFamilies = () => {
        const requestedFamilies = Number(familyInput);
        if (!familyInput.trim() || !Number.isFinite(requestedFamilies)) {
            applyCapacity(capacity);
            return;
        }
        const nextCapacity = getPremiumCapacityForFamilies(requestedFamilies, config);
        const rounded = nextCapacity.familyLicenses !== requestedFamilies;
        applyCapacity(
            nextCapacity,
            rounded
                ? `${requestedFamilies} family licences requires ${nextCapacity.units} whole unit${nextCapacity.units === 1 ? '' : 's'}. Rounded up to ${nextCapacity.familyLicenses} family licences and ${nextCapacity.dashboardSeats} dashboard seats.`
                : ''
        );
    };

    const unitPrice = interval === 'monthly' ? plan.monthlyDisplayPrice : plan.annualDisplayPrice;
    const totalPrice = getPremiumTotalPrice(unitPrice, capacity.units);
    const intervalUnit = interval === 'monthly' ? 'month' : 'year';
    const signupSearch = new URLSearchParams({
        plan: plan.planCode,
        version: plan.versionId,
        interval,
        units: String(capacity.units),
    });
    const enterpriseContact = `mailto:support@superpenguin.com?subject=${encodeURIComponent(
        'SuperPenguin enterprise licence'
    )}&body=${encodeURIComponent(
        `We need ${capacity.dashboardSeats} dashboard seats and approximately ${capacity.familyLicenses} family licences.`
    )}`;

    return (
        <section className='saas-premium-card' aria-labelledby='premium-heading'>
            <header className='saas-premium-card__header'>
                <div>
                    <span className='saas-eyebrow'>Premium</span>
                    <h2 id='premium-heading'>One plan. Everything included.</h2>
                    <p>Size it to your practice. Each unit adds {config.dashboardSeatsPerUnit} dashboard seat and {config.familyLicensesPerUnit} annual family licences.</p>
                </div>
                <div className='saas-billing-choice'>
                    <div className='saas-billing-toggle' role='group' aria-label='Billing interval'>
                        <button
                            type='button'
                            className={interval === 'monthly' ? 'is-selected' : ''}
                            aria-pressed={interval === 'monthly'}
                            onClick={() => onIntervalChange('monthly')}
                        >Monthly</button>
                        <button
                            type='button'
                            className={interval === 'annual' ? 'is-selected' : ''}
                            aria-pressed={interval === 'annual'}
                            onClick={() => onIntervalChange('annual')}
                        >Annual</button>
                    </div>
                    {typeof plan.monthlyDisplayPrice === 'number' &&
                        typeof plan.annualDisplayPrice === 'number' &&
                        plan.annualDisplayPrice < plan.monthlyDisplayPrice * 12 && (
                            <span>Two months free annually</span>
                        )}
                </div>
            </header>

            <div className='saas-capacity-controls'>
                <CapacityControl
                    id='premium-dashboard-seats'
                    label='How many team members need the dashboard?'
                    help='Therapists and practice Admins both count as dashboard seats.'
                    value={seatInput}
                    minimum={config.minimumUnits * config.dashboardSeatsPerUnit}
                    step={config.dashboardSeatsPerUnit}
                    decreaseDisabled={capacity.units <= config.minimumUnits}
                    increaseDisabled={capacity.units > config.maximumSelfServeUnits}
                    onChange={setSeatInput}
                    onCommit={commitSeats}
                    onDecrease={() => selectUnits(capacity.units - 1)}
                    onIncrease={() => selectUnits(capacity.units + 1)}
                />

                <div className='saas-capacity-link' aria-hidden='true'>
                    <span>{config.dashboardSeatsPerUnit} seat = {config.familyLicensesPerUnit} families</span>
                </div>

                <CapacityControl
                    id='premium-family-licences'
                    label='How many families will you support each year?'
                    help='Type any requirement and we will round it up to the next whole unit.'
                    value={familyInput}
                    minimum={config.minimumUnits * config.familyLicensesPerUnit}
                    step={config.familyLicensesPerUnit}
                    decreaseDisabled={capacity.units <= config.minimumUnits}
                    increaseDisabled={capacity.units > config.maximumSelfServeUnits}
                    onChange={setFamilyInput}
                    onCommit={commitFamilies}
                    onDecrease={() => selectUnits(capacity.units - 1)}
                    onIncrease={() => selectUnits(capacity.units + 1)}
                />
                <p id='capacity-adjustment-message' className='saas-capacity-message' aria-live='polite'>
                    {adjustmentMessage}
                </p>
            </div>

            <div className={`saas-capacity-result${capacity.exceedsSelfServeMaximum ? ' saas-capacity-result--enterprise' : ''}`}>
                <div>
                    <span className='saas-result-label'>What you get</span>
                    <strong>
                        {capacity.dashboardSeats} dashboard seat{capacity.dashboardSeats === 1 ? '' : 's'}
                        <span aria-hidden='true'> · </span>
                        {capacity.familyLicenses} family licences per year
                    </strong>
                </div>
                {capacity.exceedsSelfServeMaximum ? (
                    <div className='saas-enterprise-result'>
                        <strong>Let’s tailor this to your organisation</strong>
                        <span>Online checkout supports up to {config.maximumSelfServeUnits} units.</span>
                    </div>
                ) : (
                    <div className='saas-capacity-price' aria-label={totalPrice === undefined
                        ? 'Pricing unavailable'
                        : `${formatPrice(totalPrice)} per ${intervalUnit}, excluding VAT`}>
                        {totalPrice === undefined ? (
                            <strong>Pricing unavailable</strong>
                        ) : (
                            <><strong>{formatPrice(totalPrice)}</strong><span>per {intervalUnit}, excluding VAT</span></>
                        )}
                    </div>
                )}
            </div>

            {capacity.exceedsSelfServeMaximum ? (
                <a
                    className='saas-button saas-button--wide'
                    href={enterpriseContact}
                    target={navigationTarget}
                >Talk to us</a>
            ) : totalPrice === undefined ? (
                <span className='saas-button saas-button--wide saas-button--disabled' aria-disabled='true'>Temporarily unavailable</span>
            ) : (
                <Link
                    className='saas-button saas-button--wide'
                    to={`/signup?${signupSearch.toString()}`}
                    target={navigationTarget}
                >Buy Premium</Link>
            )}
            <p className='saas-checkout-note'>Have a percentage discount code? Enter it at secure Stripe Checkout.</p>
            <p className='saas-renewal-note'>Family licences reset every 12 months. Unused licences do not carry over.</p>

            <div className='saas-premium-inclusions'>
                <span className='saas-result-label'>Included at every size</span>
                <ul className='saas-feature-list saas-feature-list--columns'>
                    <li><Check /> Unlimited Audio Scribe recordings</li>
                    <li><Check /> Unlimited AI-generated reports</li>
                    <li><Check /> Full clinician dashboard and caseload</li>
                    <li><Check /> The SuperPenguin app for every licensed family</li>
                    <li><Check /> Progress and engagement data per family</li>
                    <li><Check /> Reusable activation keys pooled across your practice</li>
                    <li><Check /> SMS and email campaigns to families</li>
                    <li><Check /> Support and onboarding from SuperPenguin</li>
                </ul>
            </div>
        </section>
    );
};

// Keep the free option visible without competing with the primary configurable offer.
const FreeTrialCard: React.FC<{ plan: PublicPlan; navigationTarget?: '_top' }> = ({ plan, navigationTarget }) => {
    const signupSearch = new URLSearchParams({
        plan: plan.planCode,
        version: plan.versionId,
        interval: 'monthly',
        units: String(plan.minimumUnits),
    });
    return (
        <section className='saas-free-trial' aria-labelledby='free-trial-heading'>
            <div>
                <span className='saas-eyebrow'>No card needed</span>
                <h2 id='free-trial-heading'>Not ready to buy? Start with a Free trial.</h2>
                <p>
                    {plan.dashboardSeatsPerUnit} dashboard seat, {plan.familyLicensesPerUnit} family licences per year,
                    {' '}{plan.usageLimits.audioScribeRecordings} Audio Scribe recordings and
                    {' '}{plan.usageLimits.aiGeneratedReports} AI-generated reports.
                </p>
            </div>
            <Link
                className='saas-button saas-button--outline'
                to={`/signup?${signupSearch.toString()}`}
                target={navigationTarget}
            >Start free</Link>
        </section>
    );
};

export const PlansPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
    const [interval, selectBillingInterval] = useState<BillingInterval>('monthly');
    const { catalog, isLoading, error, reload } = usePublicPlanCatalog();
    const plans = useMemo(() => catalog?.plans || [], [catalog]);
    const freeTrial = plans.find((plan) => plan.planCode === 'starter');
    const premium = plans.find((plan) => plan.planCode === 'premium');

    if (isLoading) {
        return <PublicLayout showChrome={!embedded}><PublicLoading message='Loading plans…' /></PublicLayout>;
    }
    if (error || !freeTrial || !premium) {
        return (
            <PublicLayout showChrome={!embedded}>
                <PublicError
                    title='Plans are temporarily unavailable'
                    message={error || 'The current Free trial and Premium plans are not both available.'}
                    onRetry={reload}
                    returnTarget={embedded ? '_top' : undefined}
                />
            </PublicLayout>
        );
    }

    return (
        <PublicLayout showChrome={!embedded}>
            <main>
                <section className='saas-hero saas-hero--pricing' aria-labelledby='plans-heading'>
                    <span className='saas-eyebrow'>For private speech and language therapy practices</span>
                    <h1 id='plans-heading'>Stop writing up your sessions.</h1>
                    <p>SuperPenguin listens, drafts your notes and reports, and gives every family a guided programme to work on between appointments.</p>
                </section>

                <PremiumConfigurator
                    key={premium.versionId}
                    plan={premium}
                    interval={interval}
                    onIntervalChange={selectBillingInterval}
                    navigationTarget={embedded ? '_top' : undefined}
                />
                <FreeTrialCard plan={freeTrial} navigationTarget={embedded ? '_top' : undefined} />

                <section className='saas-explainer' aria-labelledby='family-licenses-heading'>
                    <div>
                        <span className='saas-eyebrow'>Simple annual allowance</span>
                        <h2 id='family-licenses-heading'>One care recipient, one family licence</h2>
                    </div>
                    <div>
                        <p>A family licence covers one care recipient and all of their carers. Additional carers do not use another licence.</p>
                        <p>Families can still join with a valid activation key if the balance becomes negative. The organisation has 30 days to increase Premium capacity before its dashboard is locked; family access to SuperPenguin continues.</p>
                    </div>
                </section>

                <section className='saas-enterprise' aria-labelledby='enterprise-heading'>
                    <div>
                        <span className='saas-eyebrow'>Enterprise or NHS</span>
                        <h2 id='enterprise-heading'>Need more than 20 units or a custom agreement?</h2>
                        <p>We continue to support NHS trusts and larger organisations through our existing sales-led service, with custom access, onboarding and agreements.</p>
                    </div>
                    <a
                        className='saas-button saas-button--light'
                        href='mailto:support@superpenguin.com?subject=Enterprise%20or%20NHS%20SuperPenguin'
                        target={embedded ? '_top' : undefined}
                    >Talk to us</a>
                </section>

                <section className='saas-section saas-faq' aria-labelledby='faq-heading'>
                    <div className='saas-section-heading'>
                        <span className='saas-eyebrow'>Questions</span>
                        <h2 id='faq-heading'>Frequently asked</h2>
                    </div>
                    <div className='saas-faq-grid'>
                        <details><summary>How do subscription units work?</summary><p>Each Premium unit includes one dashboard seat and 50 family licences per year. Increase either number and the linked allowance updates automatically.</p></details>
                        <details><summary>What happens if we use all our family licences?</summary><p>Families can still join with a valid activation key. Your balance can become negative and Admins have 30 days to increase capacity before the clinic dashboard is locked. Family app access continues.</p></details>
                        <details><summary>When do family allowances renew?</summary><p>Family allowances reset every 12 months, including when Premium is paid monthly. Unused licences do not carry over.</p></details>
                        <details><summary>Can we change or cancel Premium?</summary><p>Owners can manage subscription capacity, billing, invoices and cancellation from the dashboard.</p></details>
                        <details><summary>Can I use a discount code?</summary><p>Yes. Enter an eligible percentage discount code during secure Stripe Checkout.</p></details>
                        <details><summary>Are prices inclusive of VAT?</summary><p>No. Prices on this page exclude VAT. Stripe calculates and displays applicable tax before payment.</p></details>
                    </div>
                </section>

                <section className='saas-final-cta'>
                    <h2>Ready to spend more time with families?</h2>
                    <p>
                        Configure Premium above, start a Free trial, or{' '}
                        <Link to='/' target={embedded ? '_top' : undefined}>sign in to your existing account</Link>.
                    </p>
                </section>
            </main>
        </PublicLayout>
    );
};
